import { useCallback, useEffect } from 'react';
import { useTranslation } from 'react-i18next';
import { useForm } from 'react-hook-form';

import { Button, Divider, Input, IconButton } from '@treezor/design-system';

import { useAppSelector, useAppDispatch } from '@/hooks/redux-helpers';
import { useInputs } from '@/hooks/useInputs';

import { setBulkCardCreationStep } from '@/store/actions/bulkCardCreations.actions';
import { openModal } from '@/store/actions/core.actions';
import { useDashboardConfig } from '@/hooks/useDashboardConfig';
import { resetPredefRestrictionsGroups } from '@/store/actions/predef-restrictions-group.actions';

import { useCardsLimitsFields } from '@/features/cards/hooks/useCardLimits';

import { TCardLimits } from '@/features/cards/store/models/card.model';

import { IBulkCardRestrictionsProps } from './BulkCardRestrictionsModal';
import { IBulkCardLimitsProps } from './BulkCardLimitsModal';

interface IBulkOptionsFormInputs {
  logoId: string;
  packageId: string;
  designCode: string;
  batchDeliveryId: number;
}

export const BulkOptions = () => {
  const { t } = useTranslation();
  const dispatch = useAppDispatch();
  const config = useDashboardConfig();
  const activeStep = useAppSelector((state) => state.bulkCardCreation.currentStep);
  const bulkCardCreationDraft = useAppSelector((state) => state.bulkCardCreation.bulkCardCreationDraft);

  const defaultValues: IBulkOptionsFormInputs = {
    logoId: bulkCardCreationDraft?.logoId || '',
    packageId: bulkCardCreationDraft?.packageId || '',
    designCode: bulkCardCreationDraft?.designCode || '',
    batchDeliveryId: bulkCardCreationDraft?.batchDeliveryId || undefined,
  };
  const formMethods = useForm<IBulkOptionsFormInputs>({ defaultValues, mode: 'onChange' });

  const { limits, normalizeValue } = useCardsLimitsFields('BULK');

  const {
    handleSubmit,
    watch,
    trigger,
    formState: { errors },
  } = formMethods;

  const methods = useInputs(
    [
      {
        name: 'logoId',
        type: 'text',
        options: {
          maxLength: { value: 30, message: t('pages.administration.bulkCards.bulkoptions.rules.logoId') },
        },
      },
      {
        name: 'packageId',
        type: 'text',
        options: {
          maxLength: { value: 8, message: t('pages.administration.bulkCards.bulkoptions.rules.packageId') },
        },
      },
      {
        name: 'designCode',
        type: 'text',
        options: {
          maxLength: { value: 2, message: t('pages.administration.bulkCards.bulkoptions.rules.designCode') },
        },
      },
      {
        name: 'batchDeliveryId',
        type: 'number',
        options: {
          min: { value: 1, message: t('pages.administration.bulkCards.bulkoptions.rules.batchDeliveryId') },
          max: { value: 238327, message: t('pages.administration.bulkCards.bulkoptions.rules.batchDeliveryId') },
        },
      },
    ],
    formMethods,
  );

  const toggleAttachBulkCardRestrictions = () => {
    dispatch(resetPredefRestrictionsGroups(['mid', 'mcc', 'country']));
    dispatch(openModal<IBulkCardRestrictionsProps>('bulkCardRestrictionModal', { activeStep }));
  };
  const toggleAttachBulkCardLimits = () => {
    dispatch(openModal<IBulkCardLimitsProps>('bulkdCardLimnitModal', { activeStep }, { maxWidth: 'lg' }));
  };

  const onSubmit = useCallback(
    (data: IBulkOptionsFormInputs) => {
      const isLimitValueEmpty = Object.keys(limits).every((key) => bulkCardCreationDraft[key] === undefined);
      const nomalizedValues = isLimitValueEmpty
        ? Object.keys(limits).reduce((prev, current: keyof TCardLimits) => {
            return {
              [current]: normalizeValue(current, limits[current]),
              ...prev,
            };
          }, {})
        : {};
      dispatch(setBulkCardCreationStep(activeStep + 1, { ...data, ...nomalizedValues }));
    },
    [activeStep, bulkCardCreationDraft, dispatch, limits, normalizeValue],
  );

  const logoIdValue = watch('logoId');
  const packageIdvalue = watch('packageId');
  const designCodeValue = watch('designCode');
  const batchDeliveryIdValue = watch('batchDeliveryId');

  useEffect(() => {
    trigger();
  }, [logoIdValue, packageIdvalue, trigger]);

  return (
    <>
      <h5 className="typo-h5-bold text-secondary">{t('pages.administration.bulkCards.bulkoptions.title')}</h5>

      <form className="flex flex-col gap-6 mt-7 w-[320px]" onSubmit={handleSubmit(onSubmit)}>
        <Input
          type="text"
          placeholder={t('pages.administration.bulkCards.bulkoptions.logoId')}
          name="logoId"
          onChange={methods.handleLogoIdChange}
          error={errors.logoId?.message}
          maxLengthValue={30}
          value={logoIdValue || ''}
        />

        <Input
          type="text"
          placeholder={t('pages.administration.bulkCards.bulkoptions.packageId')}
          name="packageId"
          onChange={methods.handlePackageIdChange}
          error={errors.packageId?.message}
          maxLengthValue={8}
          value={packageIdvalue || ''}
        />
        {config?.settings?.bulkCards?.batchDelivery && (
          <Input
            type="number"
            placeholder={t('pages.administration.bulkCards.bulkoptions.batchDeliveryId')}
            name="batchDeliveryId"
            onChange={methods.handleBatchDeliveryIdChange}
            error={errors.batchDeliveryId?.message}
            value={batchDeliveryIdValue?.toString()}
          />
        )}

        <Input
          type="text"
          placeholder={t('pages.administration.bulkCards.bulkoptions.designCode')}
          name="designCode"
          onChange={methods.handleDesignCodeChange}
          error={errors.designCode?.message}
          maxLengthValue={2}
          value={designCodeValue || ''}
        />

        <div className="flex flex-col gap-5">
          <div className="flex items-center justify-between">
            <div className="text-secondary">
              <p className="typo-body-bold-paragraph">
                {t('pages.administration.bulkCards.bulkoptions.sectionRestriction')}
              </p>
              <span className="typo-body-regular-small">
                {t('pages.administration.bulkCards.bulkoptions.subSectionRestriction')}
              </span>
            </div>
            <IconButton
              icon="arrow_forward"
              onClick={toggleAttachBulkCardRestrictions}
              data-testid="bulk-restriction-option"
            />
          </div>

          <Divider />

          <div className="flex items-center justify-between">
            <div className="text-secondary">
              <p className="typo-body-bold-paragraph">
                {t('pages.administration.bulkCards.bulkoptions.sectionPaiement')}
              </p>
              <span className="typo-body-regular-small">
                {t('pages.administration.bulkCards.bulkoptions.subSectionPaiement')}
              </span>
            </div>
            <IconButton icon="arrow_forward" onClick={toggleAttachBulkCardLimits} data-testid="bulk-limit-option" />
          </div>
        </div>
        <div className="flex items-center justify-center mt-3">
          <Button id="next-bulk-cards-option-btn" type="submit">
            {t('pages.administration.bulkCards.next')}
          </Button>
        </div>
      </form>
    </>
  );
};
